/**
 * The spec §7.1 breakpoint ladder, as one source of truth.
 *
 * Every threshold in this file is measured against **available content width**,
 * never the viewport. The native app branches on measured layout size, which
 * under Split View and Stage Manager is the window rather than the screen; a
 * web build that branched on viewport width would render the wide composition
 * inside a narrow pane the moment a feature is nested in a split. That is why
 * `resolveLayout` takes a size it is handed and never reads a global.
 *
 * Nothing here touches React or the DOM: the ladder is arithmetic, and the
 * arithmetic is what feature packets must be able to test without a renderer.
 */

// ---------------------------------------------------------------------------
// The ladder
// ---------------------------------------------------------------------------

/**
 * The fifteen named thresholds of §7.1. Feature packets branch on the **name**,
 * never on the literal — a literal is how a component silently drifts off the
 * ladder when a threshold moves.
 */
export type BreakpointName =
  | "studioMinimum"
  | "workspace"
  | "readable"
  | "trainTwoColumn"
  | "historySplit"
  | "cornerDesk"
  | "comboLabThreePane"
  | "buildSplit"
  | "buildToolRail"
  | "liveBoard"
  | "studioFull"
  | "tapeThreePane"
  | "libraryCap"
  | "cornerDeskCap"
  | "trainCap";

export const BREAKPOINTS = {
  /** `studioMinimumWidth`. Split shell begins; stacked live board eligible. */
  studioMinimum: 620,
  /** `workspaceWidth`. Two-zone workspaces (Build hub, Combo Lab, Settings, Combo Studio `.half`). */
  workspace: 680,
  /** `readableWidth`. Max measure for reading-oriented screens. */
  readable: 760,
  /** Corner Desk training column + briefing column. */
  trainTwoColumn: 850,
  /** History table + detail. */
  historySplit: 880,
  /** `cornerDesk` window class / `writingWidth`. Persistent canvas + contextual rail. */
  cornerDesk: 900,
  /** Combo Lab three-pane: favorites | score | constraints. */
  comboLabThreePane: 920,
  /** Build split: source | canvas | inspector. */
  buildSplit: 930,
  /** Persistent Build tool rail vs segmented picker. */
  buildToolRail: 960,
  /** Two-panel Ringside Board — width only; see `HEIGHT_THRESHOLDS` and the aspect rule. */
  liveBoard: 980,
  /** Combo Studio `.full` three-pane compose / sidebar auto-show. */
  studioFull: 1000,
  /** Workout Tape three-pane: add rail | canvas | inspector + library. */
  tapeThreePane: 1020,
  /** Drill library / Combo Lab content cap. */
  libraryCap: 1080,
  /** Corner Desk cap: builder / Train workspace max width. */
  cornerDeskCap: 1120,
  /** Train cap. */
  trainCap: 1180,
} as const satisfies Record<BreakpointName, number>;

export interface BreakpointLadderEntry {
  readonly name: BreakpointName;
  readonly px: number;
  /** The §7.1 "Effect" column, verbatim enough to be recognisable in a review. */
  readonly effect: string;
  /**
   * Whether accessibility Dynamic Type collapses this threshold (§8.1, §8.3).
   *
   * `studioMinimum` is the one composition threshold that survives: §8.3 forces
   * the sidebar to detail-only and *widens* the sidebar columns, which is only
   * meaningful if the split shell still exists. What accessibility kills is the
   * multi-pane content and the automatic sidebar, not the shell chrome.
   *
   * Max-measure thresholds (`readable`, `libraryCap`, `cornerDeskCap`,
   * `trainCap`) also survive: they cap a reading measure rather than divide a
   * composition, and collapsing them would make lines *longer* at the exact
   * text size where short lines matter most.
   */
  readonly collapsesAtAccessibilityTextSize: boolean;
}

/** The ladder in ascending px order. `breakpoints.test.ts` asserts the order. */
export const BREAKPOINT_LADDER: readonly BreakpointLadderEntry[] = [
  {
    name: "studioMinimum",
    px: BREAKPOINTS.studioMinimum,
    effect: "Split shell begins; stacked live board becomes eligible (with height >= 620)",
    collapsesAtAccessibilityTextSize: false,
  },
  {
    name: "workspace",
    px: BREAKPOINTS.workspace,
    effect: "Two-zone workspaces (Build hub, Combo Lab, Settings, Combo Studio .half)",
    collapsesAtAccessibilityTextSize: true,
  },
  {
    name: "readable",
    px: BREAKPOINTS.readable,
    effect: "Max measure for reading-oriented screens",
    collapsesAtAccessibilityTextSize: false,
  },
  {
    name: "trainTwoColumn",
    px: BREAKPOINTS.trainTwoColumn,
    effect: "Corner Desk training column + briefing column",
    collapsesAtAccessibilityTextSize: true,
  },
  {
    name: "historySplit",
    px: BREAKPOINTS.historySplit,
    effect: "History table + detail",
    collapsesAtAccessibilityTextSize: true,
  },
  {
    name: "cornerDesk",
    px: BREAKPOINTS.cornerDesk,
    effect: "Persistent primary canvas + contextual rail; Journal three-pane; writing measure",
    collapsesAtAccessibilityTextSize: true,
  },
  {
    name: "comboLabThreePane",
    px: BREAKPOINTS.comboLabThreePane,
    effect: "Combo Lab three-pane: favorites | score | constraints",
    collapsesAtAccessibilityTextSize: true,
  },
  {
    name: "buildSplit",
    px: BREAKPOINTS.buildSplit,
    effect: "Build split: source | canvas | inspector",
    collapsesAtAccessibilityTextSize: true,
  },
  {
    name: "buildToolRail",
    px: BREAKPOINTS.buildToolRail,
    effect: "Persistent Build tool rail vs segmented picker",
    collapsesAtAccessibilityTextSize: true,
  },
  {
    name: "liveBoard",
    px: BREAKPOINTS.liveBoard,
    effect: "Two-panel Ringside Board (also needs height >= 620 and aspect >= 1.10)",
    collapsesAtAccessibilityTextSize: true,
  },
  {
    name: "studioFull",
    px: BREAKPOINTS.studioFull,
    effect: "Combo Studio .full three-pane compose; sidebar auto-show iff landscape and non-accessibility",
    collapsesAtAccessibilityTextSize: true,
  },
  {
    name: "tapeThreePane",
    px: BREAKPOINTS.tapeThreePane,
    effect: "Workout Tape three-pane: add rail | canvas | inspector + library",
    collapsesAtAccessibilityTextSize: true,
  },
  {
    name: "libraryCap",
    px: BREAKPOINTS.libraryCap,
    effect: "Drill library / Combo Lab content max width",
    collapsesAtAccessibilityTextSize: false,
  },
  {
    name: "cornerDeskCap",
    px: BREAKPOINTS.cornerDeskCap,
    effect: "Builder / Train workspace max width",
    collapsesAtAccessibilityTextSize: false,
  },
  {
    name: "trainCap",
    px: BREAKPOINTS.trainCap,
    effect: "Train max width",
    collapsesAtAccessibilityTextSize: false,
  },
];

// ---------------------------------------------------------------------------
// Measures, heights, aspect
// ---------------------------------------------------------------------------

/**
 * Max content measures. These are caps, not composition thresholds, which is
 * why they are a separate map even though every value is also a rung on the
 * ladder: 900 is simultaneously the `cornerDesk` window class and the
 * `writingWidth` measure, and a component asking for one must not get the
 * other's semantics by accident.
 */
export const MEASURE_CAPS = {
  /** `readableWidth`: About, Cues, Drills, Onboarding, Builder, Home, History, Journal, Summary. */
  readable: BREAKPOINTS.readable,
  /** `writingWidth`: the Journal writing canvas. */
  writing: BREAKPOINTS.cornerDesk,
  /** Drill library and Combo Lab. */
  library: BREAKPOINTS.libraryCap,
  /** Corner Desk: builder and Train workspace. */
  cornerDesk: BREAKPOINTS.cornerDeskCap,
  /** Train. */
  train: BREAKPOINTS.trainCap,
} as const;

export type MeasureName = keyof typeof MEASURE_CAPS;

/**
 * §7.1 height thresholds. Height is the axis the ladder is usually silent
 * about, and all three of these exist because a round clock and a two-panel
 * board fail on short viewports long before they fail on narrow ones.
 */
export const HEIGHT_THRESHOLDS = {
  /** The live board — stacked or two-panel — requires at least this height. */
  liveBoardMinimum: 620,
  /** Below this, the compact live layout shrinks the clock (non-accessibility). */
  compactClock: 700,
  /** Below this, accessibility text size renders the clock before the phase header. */
  clockBeforeHeader: 760,
} as const;

/** §7.1: the two-panel board additionally requires aspect >= 1.10. */
export const LIVE_BOARD_MINIMUM_ASPECT_RATIO = 1.1;

/**
 * The aspect test is a cross-multiplication rather than `width / height >= 1.1`
 * because 1.1 is not representable in IEEE-754: the division form rejects sizes
 * that are exactly on the documented boundary.
 */
const ASPECT_NUMERATOR = 11;
const ASPECT_DENOMINATOR = 10;

function meetsLiveBoardAspect(width: number, height: number): boolean {
  return width * ASPECT_DENOMINATOR >= height * ASPECT_NUMERATOR;
}

// ---------------------------------------------------------------------------
// Fixed column widths
// ---------------------------------------------------------------------------

/**
 * Columns whose width is a single fixed number in §7.1 (and, for
 * `workspacePicker`, §8.3). They are constants rather than tokens because they
 * are structural measurements of one named surface, not reusable design values.
 */
export const FIXED_COLUMN_WIDTHS = {
  buildToolRail: 246,
  tapeAddRail: 196,
  tapeInspector: 278,
  studioLibrary: 214,
  studioInspector: 292,
  journalLibraryRail: 420,
  settingsSidebar: 318,
  buildEditorInspector: 330,
  comboLab: 330,
  /** §8.3: the workspace picker widens to 420 at accessibility text sizes. */
  workspacePicker: 420,
} as const;

/**
 * A resizable split column: minimum, preferred, maximum. `ideal` is `null` for
 * the Journal reader, which §7.1 states as a min/max pair only — inventing a
 * preferred width there would put a number in the product the spec never gave.
 */
export interface ColumnWidths {
  readonly min: number;
  readonly ideal: number | null;
  readonly max: number;
}

export const SPLIT_COLUMN_WIDTHS = {
  sidebar: { min: 232, ideal: 258, max: 292 },
  /** §7.1 / §8.3: the sidebar widens rather than narrows at accessibility sizes. */
  sidebarAccessibility: { min: 240, ideal: 290, max: 340 },
  journalCollection: { min: 250, ideal: 300, max: 370 },
  journalReader: { min: 390, ideal: null, max: 580 },
  journalInspector: { min: 220, ideal: 270, max: 330 },
} as const satisfies Record<string, ColumnWidths>;

/** The sidebar is the one column whose widths the accessibility branch changes. */
export function sidebarColumnWidths(accessibilityTextSize: boolean): ColumnWidths {
  return accessibilityTextSize
    ? SPLIT_COLUMN_WIDTHS.sidebarAccessibility
    : SPLIT_COLUMN_WIDTHS.sidebar;
}

/**
 * §7.1: the live board detail column is `min(680, max(450, w * 0.52))` with a
 * combo and `min(420, max(310, w * 0.34))` without one. It is a formula rather
 * than a fixed width because the board must keep the clock legible while the
 * detail column grows, and the two clamps differ by more than a scale factor.
 */
export function liveBoardDetailColumnWidth(
  availableWidth: number,
  hasCombo: boolean,
): number {
  return hasCombo
    ? Math.min(680, Math.max(450, availableWidth * 0.52))
    : Math.min(420, Math.max(310, availableWidth * 0.34));
}

// ---------------------------------------------------------------------------
// Classes
// ---------------------------------------------------------------------------

/**
 * The §7.1 window classes. `focus` and `cornerDesk` are named in the table;
 * `studio` is the band between them, opened by `studioMinimumWidth`.
 *
 * This is a **width-only** derivation. Accessibility text size never changes
 * the window class — it collapses compositions inside it (§8.3), and a shell
 * that dropped back to the phone dock at 1400 px would contradict the §8.3
 * row that widens the sidebar columns instead of removing them.
 */
export type WindowClass = "focus" | "studio" | "cornerDesk";

export function resolveWindowClass(width: number): WindowClass {
  if (width < BREAKPOINTS.studioMinimum) return "focus";
  if (width < BREAKPOINTS.cornerDesk) return "studio";
  return "cornerDesk";
}

/** The §7.2–7.4 bands: phone, tablet, desktop. Also width-only. */
export type SizeClass = "compact" | "tablet" | "desktop";

export function resolveSizeClass(width: number): SizeClass {
  if (width < BREAKPOINTS.studioMinimum) return "compact";
  if (width < BREAKPOINTS.tapeThreePane) return "tablet";
  return "desktop";
}

/**
 * §7.3: 620–999 renders detail-only with an explicit persistent toggle; the
 * sidebar auto-shows only at >= 1000 **and** landscape **and** non-accessibility.
 */
export type SidebarPresentation = "dock" | "toggle" | "auto";

/** The live board is a three-state decision, not a boolean (§7.1, §7.3). */
export type LiveBoardLayout = "unavailable" | "stacked" | "twoPanel";

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

export interface LayoutEnvironment {
  /** Available **content** width in CSS px. */
  readonly width: number;
  /** Available content height in CSS px. */
  readonly height: number;
  /**
   * §8.1's structural branch. It is passed in rather than measured here so the
   * ladder stays pure and so a test can pin it without a media query.
   */
  readonly accessibilityTextSize: boolean;
}

export interface LayoutResolution extends LayoutEnvironment {
  readonly windowClass: WindowClass;
  readonly sizeClass: SizeClass;
  /** width >= height. Used only for the §7.1 sidebar auto-show rule. */
  readonly landscape: boolean;
  /**
   * The single question a feature asks before choosing a composition: "must I
   * render the one-column form?". True below `studioMinimum`, and true at any
   * width under accessibility text size (§8.2 rule 1).
   */
  readonly isCompact: boolean;
  /**
   * Per-threshold reachability with the accessibility collapse already applied.
   * A feature reads `reached.buildToolRail` and gets `false` at 1400 px under
   * accessibility text size without re-deriving §8.3 for itself.
   */
  readonly reached: Readonly<Record<BreakpointName, boolean>>;
  readonly sidebar: SidebarPresentation;
  readonly liveBoard: LiveBoardLayout;
  /** §7.1: the compact live clock shrinks below 700 px height (non-accessibility). */
  readonly liveClockShrunk: boolean;
  /** §7.1: accessibility text size reorders clock-before-header below 760 px height. */
  readonly liveClockBeforeHeader: boolean;
}

export function resolveLayout(environment: LayoutEnvironment): LayoutResolution {
  const { width, height, accessibilityTextSize } = environment;

  const windowClass = resolveWindowClass(width);
  const sizeClass = resolveSizeClass(width);
  const landscape = width >= height;

  const reached = {} as Record<BreakpointName, boolean>;
  for (const entry of BREAKPOINT_LADDER) {
    const collapsed = accessibilityTextSize && entry.collapsesAtAccessibilityTextSize;
    reached[entry.name] = !collapsed && width >= entry.px;
  }

  const isCompact = windowClass === "focus" || accessibilityTextSize;

  const sidebar: SidebarPresentation =
    windowClass === "focus"
      ? "dock"
      : reached.studioFull && landscape
        ? "auto"
        : "toggle";

  const tallEnoughForBoard = height >= HEIGHT_THRESHOLDS.liveBoardMinimum;
  const liveBoard: LiveBoardLayout = accessibilityTextSize
    ? // §7.2: the live workout is compact regardless of anything else at
      // accessibility text sizes, so no board layout is available at all.
      "unavailable"
    : !tallEnoughForBoard || width < BREAKPOINTS.studioMinimum
      ? "unavailable"
      : width >= BREAKPOINTS.liveBoard && meetsLiveBoardAspect(width, height)
        ? "twoPanel"
        : "stacked";

  return {
    width,
    height,
    accessibilityTextSize,
    windowClass,
    sizeClass,
    landscape,
    isCompact,
    reached,
    sidebar,
    liveBoard,
    liveClockShrunk: !accessibilityTextSize && height < HEIGHT_THRESHOLDS.compactClock,
    liveClockBeforeHeader:
      accessibilityTextSize && height < HEIGHT_THRESHOLDS.clockBeforeHeader,
  };
}

// ---------------------------------------------------------------------------
// The CSS custom-property contract
// ---------------------------------------------------------------------------

function kebab(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

/**
 * The ladder expressed as CSS custom properties.
 *
 * CSS cannot read a custom property inside a `@media` or `@container`
 * condition, so a stylesheet still writes the literal in its condition — which
 * is exactly where drift happens. The defence is that this map is *generated*
 * from the constants above and `cssContract.test.ts` asserts `layout.css`
 * declares precisely these names and values. A stylesheet that moves a
 * threshold without moving the constant fails that test.
 */
function buildCustomProperties(): Readonly<Record<string, string>> {
  const properties: Record<string, string> = {};

  for (const [name, px] of Object.entries(BREAKPOINTS)) {
    properties[`--ff-bp-${kebab(name)}`] = `${px}px`;
  }
  for (const [name, px] of Object.entries(MEASURE_CAPS)) {
    properties[`--ff-measure-${kebab(name)}`] = `${px}px`;
  }
  for (const [name, px] of Object.entries(HEIGHT_THRESHOLDS)) {
    properties[`--ff-height-${kebab(name)}`] = `${px}px`;
  }
  for (const [name, px] of Object.entries(FIXED_COLUMN_WIDTHS)) {
    properties[`--ff-column-${kebab(name)}`] = `${px}px`;
  }
  for (const [name, widths] of Object.entries(SPLIT_COLUMN_WIDTHS)) {
    properties[`--ff-column-${kebab(name)}-min`] = `${widths.min}px`;
    if (widths.ideal !== null) {
      properties[`--ff-column-${kebab(name)}-ideal`] = `${widths.ideal}px`;
    }
    properties[`--ff-column-${kebab(name)}-max`] = `${widths.max}px`;
  }
  properties["--ff-aspect-live-board"] = String(LIVE_BOARD_MINIMUM_ASPECT_RATIO);

  return Object.freeze(properties);
}

export const LAYOUT_CSS_CUSTOM_PROPERTIES = buildCustomProperties();
